import React, { useEffect, useState } from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';

import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main } from 'container/styled';
import { Cards } from 'components/cards/frame/cards-frame';
import { debtCollectionApi, DebtCollectionConfigDto } from './debtCollectionApi';
import ConfigTab from './components/ConfigTab';
import RulesTab from './components/RulesTab';
import ChannelsTab from './components/ChannelsTab';
import TemplatesTab from './components/TemplatesTab';
import LogsTab from './components/LogsTab';
import DashboardTab from './components/DashboardTab';

const { TabPane } = Tabs;

const DebtCollectionPage: React.FC = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<DebtCollectionConfigDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    return window.location.hash.replace('#', '') || 'config';
  });

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await debtCollectionApi.getConfig();
      setConfig(data);
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    window.location.hash = key;
  };

  const configExists = config?.id != null;

  return (
    <div>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('debt-collection:title', 'Debt Collection')}
        />
      </CardToolbox>
      <Main>
        <Cards headless>
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <TabPane
              tab={
                <span>
                  <FeatherIcon icon="settings" size={16} />
                  {' '}{t('debt-collection:tabs.config', 'Configuration')}
                </span>
              }
              key="config"
            >
              <ConfigTab
                config={config}
                loading={loading}
                onConfigSaved={fetchConfig}
              />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <FeatherIcon icon="list" size={16} />
                  {' '}{t('debt-collection:tabs.rules', 'Rules')}
                </span>
              }
              key="rules"
              disabled={!configExists}
            >
              {activeTab === 'rules' && configExists && (
                <RulesTab configId={config!.id!} />
              )}
            </TabPane>

            <TabPane
              tab={
                <span>
                  <FeatherIcon icon="share-2" size={16} />
                  {' '}{t('debt-collection:tabs.channels', 'Channels')}
                </span>
              }
              key="channels"
              disabled={!configExists}
            >
              {activeTab === 'channels' && configExists && (
                <ChannelsTab />
              )}
            </TabPane>

            <TabPane
              tab={
                <span>
                  <FeatherIcon icon="file-text" size={16} />
                  {' '}{t('debt-collection:tabs.templates', 'Templates')}
                </span>
              }
              key="templates"
              disabled={!configExists}
            >
              {activeTab === 'templates' && configExists && (
                <TemplatesTab />
              )}
            </TabPane>

            <TabPane
              tab={
                <span>
                  <FeatherIcon icon="activity" size={16} />
                  {' '}{t('debt-collection:tabs.logs', 'Logs')}
                </span>
              }
              key="logs"
              disabled={!configExists}
            >
              {activeTab === 'logs' && configExists && (
                <LogsTab />
              )}
            </TabPane>

            <TabPane
              tab={
                <span>
                  <FeatherIcon icon="bar-chart-2" size={16} />
                  {' '}{t('debt-collection:tabs.dashboard', 'Dashboard')}
                </span>
              }
              key="dashboard"
              disabled={!configExists}
            >
              {activeTab === 'dashboard' && configExists && (
                <DashboardTab />
              )}
            </TabPane>
          </Tabs>
        </Cards>
      </Main>
    </div>
  );
};

export default DebtCollectionPage;
